import { useState, useEffect } from 'react';
import { StatsCard } from '../components/common/StatsCard';
import { 
  Users, 
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  FileText,
  TrendingUp,
  Activity,
  ClipboardList,
  Timer,
  Trophy,
  Play,
  Tv,
  Award
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-400">Failed to load dashboard data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Welcome back! Here's what's happening.</p>
      </div>

      {/* Lunipin App Stats - Top Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Lunipin Users"
          value={stats.total_lunipin_users?.toLocaleString() || '0'}
          icon={Users}
          trend={stats.user_growth}
          trendUp={true}
        />
        <StatsCard
          title="Daily Play Today"
          value={stats.daily_matches_today?.toLocaleString() || '0'}
          icon={Play}
        />
        <StatsCard
          title="Live Events Today"
          value={stats.live_events_today?.toLocaleString() || '0'}
          icon={Tv}
          className={stats.live_events_today > 0 ? 'border-green-500/30' : ''}
        />
        <StatsCard
          title="Events Completed"
          value={stats.total_events_completed?.toLocaleString() || '0'}
          icon={Award}
        />
      </div>

      {/* Secondary Stats - Matches & Events */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Matches Today"
          value={stats.total_matches_today || '0'}
          icon={Trophy}
        />
        <StatsCard
          title="Upcoming Events"
          value={stats.upcoming_events || '0'}
          icon={Calendar}
        />
        <StatsCard
          title="Active Employees"
          value={stats.active_employees || '0'}
          icon={CheckCircle}
        />
        <StatsCard
          title="Total Hours Logged"
          value={`${stats.total_hours || 0}h`}
          icon={Timer}
        />
      </div>

      {/* Internal Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatsCard
          title="Total Tasks"
          value={stats.total_tasks || '0'}
          icon={ClipboardList}
          trend={stats.task_completion_rate}
          trendUp={true}
        />
        <StatsCard
          title="In Progress"
          value={stats.in_progress_tasks || '0'}
          icon={Clock}
        />
        <StatsCard
          title="Timesheet Entries"
          value={stats.total_timesheets || '0'}
          icon={FileText}
        />
        <StatsCard
          title="Pending Requests"
          value={stats.pending_requests || '0'}
          icon={AlertTriangle}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Sport */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-green-400" />
            Users by Sport
          </h3>
          <div className="h-64">
            {stats.users_by_sport?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.users_by_sport} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#64748b" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={12} width={100} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '4px',
                    }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Bar dataKey="count" fill="#22c55e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No user data available
              </div>
            )}
          </div>
        </div>

        {/* Matches by Sport */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-sky-400" />
            Matches by Sport
          </h3>
          <div className="h-64">
            {stats.matches_by_sport?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.matches_by_sport}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {stats.matches_by_sport.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '4px',
                    }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No match data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Employee & Task Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employees by Department */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            Employees by Department
          </h3>
          <div className="h-48">
            {stats.employees_by_department?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.employees_by_department}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '4px',
                    }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No department data available
              </div>
            )}
          </div>
        </div>

        {/* Tasks by Status */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-yellow-400" />
            Tasks by Status
          </h3>
          <div className="h-48">
            {stats.tasks_by_status?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.tasks_by_status}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: '4px',
                    }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No task data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          Recent Activity
        </h3>
        <div className="space-y-3">
          {stats.recent_activities?.length > 0 ? (
            stats.recent_activities.map((activity) => (
              <div 
                key={activity.id}
                className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${
                    activity.type === 'user' ? 'bg-green-500' :
                    activity.type === 'match' ? 'bg-blue-500' :
                    activity.type === 'event' ? 'bg-purple-500' :
                    activity.type === 'employee' ? 'bg-cyan-500' :
                    activity.type === 'task' ? 'bg-yellow-500' : 'bg-slate-500'
                  }`}></span>
                  <p className="text-sm text-slate-300">{activity.message}</p>
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap ml-4">{activity.time}</span>
              </div>
            ))
          ) : (
            <p className="text-slate-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
}
